import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, Droplets, Leaf, Sun } from "lucide-react";
import { BRAND, IMAGES } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const PILLAR_ICONS = [Droplets, Leaf, Sun];

const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="noise-overlay relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMAGES.heroBg}
          alt={t.hero.imageAlt}
          className="h-[115%] w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#07120C] via-[#07120C]/85 to-[#07120C]/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#07120C] via-transparent to-[#07120C]/60" />

      <motion.div
        className="pointer-events-none absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-emerald-500/15 blur-3xl"
        animate={{ y: [0, 30, 0] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="pointer-events-none absolute bottom-1/4 right-10 h-56 w-56 rounded-full bg-emerald-400/10 blur-3xl"
        animate={{ y: [0, -24, 0] }}
        transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-28 pt-40 sm:px-10 lg:px-16"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-mono-tech text-xs uppercase tracking-[0.35em] text-emerald-400"
          data-testid="hero-badge"
        >
          {t.hero.badge}
        </motion.p>

        <h1
          className="font-display mt-6 max-w-4xl text-4xl font-black leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-7xl"
          data-testid="hero-headline"
        >
          {t.hero.lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "text-emerald-400" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.3 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          data-testid="hero-subheadline"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={BRAND.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-cta"
            className="group flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 text-sm font-bold text-[#07120C] transition-colors duration-300 hover:bg-emerald-400"
          >
            <MessageCircle className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
            {t.hero.cta1}
          </a>
          <a
            href="#servicos"
            data-testid="hero-explore-cta"
            className="group flex items-center gap-2 rounded-full border border-emerald-500/30 px-7 py-3.5 text-sm font-semibold text-emerald-300 transition-colors duration-300 hover:border-emerald-400/60 hover:text-emerald-200"
          >
            {t.hero.cta2}
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-16 flex flex-wrap gap-x-10 gap-y-4"
          data-testid="hero-pillars"
        >
          {t.hero.pillars.map((label, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <div key={label} className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10">
                  <Icon className="h-4 w-4 text-emerald-400" />
                </span>
                <span className="font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-300">
                  {label}
                </span>
              </div>
            );
          })}
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <ArrowDown className="h-5 w-5 text-emerald-500/70" />
      </motion.div>
    </section>
  );
};

export default Hero;
